// Client-side image compression utility.
// Resizes images to a max width (default 1920px) while preserving aspect ratio,
// and re-encodes to JPEG at a given quality (default 0.75) using a Canvas.
// Non-image files or unreadable images are returned untouched.

const DEFAULTS = {
  maxWidth: 1920,
  maxHeight: 1920,
  quality: 0.75,
  mimeType: "image/jpeg",
};

const loadImage = (file) =>
  new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = (e) => {
      URL.revokeObjectURL(url);
      reject(e);
    };
    img.src = url;
  });

export async function compressImage(file, opts = {}) {
  const { maxWidth, maxHeight, quality, mimeType } = { ...DEFAULTS, ...opts };

  if (!file || !file.type || !file.type.startsWith("image/")) return file;
  // Skip vector/animated formats that don't benefit or would break.
  if (/svg|gif/i.test(file.type)) return file;

  try {
    const img = await loadImage(file);
    const { width: w0, height: h0 } = img;
    if (!w0 || !h0) return file;

    const ratio = Math.min(1, maxWidth / w0, maxHeight / h0);
    const w = Math.round(w0 * ratio);
    const h = Math.round(h0 * ratio);

    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(img, 0, 0, w, h);

    const blob = await new Promise((resolve) =>
      canvas.toBlob(resolve, mimeType, quality)
    );
    if (!blob) return file;

    // If compression somehow produced a larger file, keep the original.
    if (blob.size >= file.size && ratio === 1) return file;

    const baseName = (file.name || "photo").replace(/\.[^.]+$/, "");
    return new File([blob], `${baseName}.jpg`, {
      type: mimeType,
      lastModified: Date.now(),
    });
  } catch {
    return file;
  }
}
